import queryString from 'querystring';
import { saveAs } from 'file-saver';

import ResponseHandler from './ResponseHandler';

class RequestService {
    getCsrfToken: () => string;
    prependDomain: (path: string) => string;
    responseHandler: ResponseHandler;

    constructor(
        responseHandler: ResponseHandler,
        getCsrfToken: () => string,
        prependDomain: (path: string) => string,
    ) {
        this.responseHandler = responseHandler;
        this.getCsrfToken = getCsrfToken;
        this.prependDomain = prependDomain;
    }

    async executeRequest(request: Request): Promise<any> {
        const response: Response = await fetch(request);
        return this.responseHandler.handleApiResponse(response);
    }

    createJsonPostRequestObject(path: string, data: any): Request {
        return new Request(this.prependDomain(path), {
            credentials: this.getCredentialsProperty(),
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(data),
        });
    }

    createFormDataPostRequestObject(url: string, data = {}, signal?: AbortSignal): Request {
        const formData = queryString.stringify({ ...data, _token: this.getCsrfToken() });
        const headers = new Headers();
        headers.append('Content-Type', 'application/x-www-form-urlencoded; charset=UTF-8');
        let requestBody: any = {
            headers,
            body: formData,
            credentials: this.getCredentialsProperty(),
            method: 'POST',
        };

        if (signal) {
            requestBody = { ...requestBody, signal };
        }

        return new Request(this.prependDomain(url), requestBody);
    }

    createJsonPostRequestWithTokenObject(path: string, data: any): Request {
        const urlWithToken = `${this.prependDomain(path)}?_token=${this.getCsrfToken()}`;
        return new Request(urlWithToken, {
            method: 'POST',
            credentials: this.getCredentialsProperty(),
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(data),
        });
    }


    createFetchRequestForExitLockdownPassword(url: string): Request {
        const headers = new Headers();
        headers.append('Content-Type', 'application/json');
        const requestBody: any = {
            method: 'POST',
            headers,
            body: JSON.stringify({}),
        };

        return new Request(this.prependDomain(`${url}?_token=${this.getCsrfToken()}`), requestBody);
    }

    createBinaryDataPostRequestObject(url: string, data: FormData, signal?: AbortSignal): Request {
        let requestBody: any = {
            body: data,
            credentials: this.getCredentialsProperty(),
            method: 'POST',
        };

        if (signal) {
            requestBody = { ...requestBody, signal };
        }

        return new Request(this.prependDomain(`${url}&_token=${this.getCsrfToken()}`), requestBody);
    }

    createFetchRequestObject(path: string, signal?: AbortSignal): Request {
        let requestBody: any = {
            credentials: this.getCredentialsProperty(),
        };

        if (signal) {
            requestBody = { ...requestBody, signal };
        }

        return new Request(this.prependDomain(path), requestBody);
    }

    createFetchUrlWithQueryParams(path: string, requestData: any, signal?: AbortSignal): Request {
        const url = new URL(this.prependDomain(path));

        let requestBody: any = {
            credentials: this.getCredentialsProperty(),
        };

        if (signal) {
            requestBody = { ...requestBody, signal };
        }
        Object.keys(requestData).forEach((key) => url.searchParams.append(key, requestData[key]));
        return new Request(url.href, requestBody);
    }

    getCredentialsProperty(): RequestCredentials {
        if (process.env.NODE_ENV === 'development' && DEV_PICK_ENV) {
            return 'include';
        }
        return 'same-origin';
    }

    // fileName needed on Windows systems
    async executeFileDownload(request: Request, fileName?: string): Promise<object | Error> {
        const response: Response = await fetch(request);
        const fileBlob: Blob = await this.responseHandler.handleApiResponse(response, true);

        try {
            saveAs(fileBlob, fileName);
            return { fileDownloaded: true };
        } catch (error) {
            return error;
        }
    }
}

export default RequestService;
